import { http, results } from './client';

const seg = (value) => encodeURIComponent(String(value));
const UPLOAD = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180_000 };
export const MAX_IMAGES_PER_UPLOAD = 10;

/* ---------- Properties ---------- */

/** Every property the user can access, each with `my_role`: owner | manager | viewer. */
export const listMyProperties = () =>
  http.get('api/v1/selfhost/hotels/my').then((r) => results(r) || []);

/** Raw property record with images, products (with product_images) and owner. */
export const getProperty = (id) => http.get(`api/v1/selfhost/hotels/${seg(id)}`).then(results);

export const createProperty = (payload) =>
  http.post('api/v1/selfhost/hotels', payload).then(results);

export const updateProperty = (id, payload) =>
  http.put(`api/v1/selfhost/hotels/${seg(id)}`, payload).then(results);

export const deleteProperty = (id) =>
  http.delete(`api/v1/selfhost/hotels/${seg(id)}`).then(results);

/* ---------- Rooms (products) ---------- */

export const listRooms = (hotelId) =>
  http.get(`api/v1/selfhost/hotels/${seg(hotelId)}/products`).then((r) => results(r) || []);

export const createRoom = (payload) =>
  http.post('api/v1/selfhost/products', payload).then(results);

export const updateRoom = (id, payload) =>
  http.put(`api/v1/selfhost/products/${seg(id)}`, payload).then(results);

export const deleteRoom = (id) => http.delete(`api/v1/selfhost/products/${seg(id)}`).then(results);

/* ---------- Images ---------- */

function uploadImages(basePath, files, { firstIsPrimary = false } = {}) {
  const list = Array.from(files).slice(0, MAX_IMAGES_PER_UPLOAD);
  const form = new FormData();
  if (list.length === 1) {
    form.append('image', list[0]);
    form.append('is_primary', firstIsPrimary ? 'true' : 'false');
    return http.post(`${basePath}/images`, form, UPLOAD).then((r) => [results(r)].filter(Boolean));
  }
  list.forEach((file) => form.append('images', file));
  return http.post(`${basePath}/images/bulk`, form, UPLOAD).then((r) => results(r) || []);
}

/** Uploads up to 10 property photos; returns the created image records. */
export const uploadPropertyImages = (hotelId, files, options) =>
  uploadImages(`api/v1/selfhost/hotels/${seg(hotelId)}`, files, options);

/** { title?, alt_text?, is_primary? } */
export const updatePropertyImage = (imageId, patch) =>
  http.put(`api/v1/selfhost/images/${seg(imageId)}`, patch).then(results);

export const deletePropertyImage = (imageId) =>
  http.delete(`api/v1/selfhost/images/${seg(imageId)}`).then(results);

/** orders: [{ imageId, order }] */
export const reorderPropertyImages = (hotelId, orders) =>
  http
    .put(`api/v1/selfhost/hotels/${seg(hotelId)}/images/reorder`, { imageOrders: orders })
    .then(results);

export const listRoomImages = (productId) =>
  http.get(`api/v1/selfhost/products/${seg(productId)}/images`).then((r) => results(r) || []);

export const uploadRoomImages = (productId, files, options) =>
  uploadImages(`api/v1/selfhost/products/${seg(productId)}`, files, options);

export const updateRoomImage = (imageId, patch) =>
  http.put(`api/v1/selfhost/product-images/${seg(imageId)}`, patch).then(results);

export const deleteRoomImage = (imageId) =>
  http.delete(`api/v1/selfhost/product-images/${seg(imageId)}`).then(results);

/** orders: [{ imageId, order }] */
export const reorderRoomImages = (productId, orders) =>
  http
    .put(`api/v1/selfhost/products/${seg(productId)}/images/reorder`, { imageOrders: orders })
    .then(results);

/* ---------- Team ---------- */

/** Active members ({ status: 'active', id, user, role }) and pending invites ({ status: 'invited', invite_id, email, role }). */
export const listMembers = (hotelId) =>
  http.get(`api/v1/selfhost/hotels/${seg(hotelId)}/members`).then((r) => results(r) || []);

export const inviteMember = (hotelId, { email, role }) =>
  http.post(`api/v1/selfhost/hotels/${seg(hotelId)}/invites`, { email, role }).then(results);

export const updateMemberRole = (hotelId, memberId, role) =>
  http.put(`api/v1/selfhost/hotels/${seg(hotelId)}/members/${seg(memberId)}`, { role }).then(results);

export const removeMember = (hotelId, memberId) =>
  http.delete(`api/v1/selfhost/hotels/${seg(hotelId)}/members/${seg(memberId)}`).then(results);

export const cancelInvite = (hotelId, inviteId) =>
  http.delete(`api/v1/selfhost/hotels/${seg(hotelId)}/invites/${seg(inviteId)}`).then(results);

/** Public. { token, hotel: { id, name, city, country }, email, role, expires_at, user_exists } */
export const getInviteInfo = (token) =>
  http.get('api/v1/selfhost/invites/info', { params: { token } }).then(results);

/** Returns { hotel, role } (or { message } when already a member). */
export const acceptInvite = (token) =>
  http.post('api/v1/selfhost/invites/accept', { token }).then(results);

/* ---------- Payouts (Stripe Connect) ---------- */

/** { status: 'not_started' | 'incomplete' | 'complete', onboarding_complete, payouts_enabled, requirements } */
export const getPayoutStatus = () => http.get('api/v1/selfhost/connect/status').then(results);

/** Returns { url } of Stripe's hosted onboarding. */
export const startPayoutOnboarding = () =>
  http.post('api/v1/selfhost/connect/onboard').then(results);

/** Returns { url } of the Stripe Express dashboard. */
export const getPayoutDashboardLink = () =>
  http.post('api/v1/selfhost/connect/dashboard').then(results);
